// ===============================================
// File: src/components/Sync/SyncPill.jsx
// Drive/Folder sync pill with per-action spinners and clear feedback
// ===============================================
import React, { useMemo, useState } from "react";
import { useStorage } from "@/contexts/StorageContext";
import { useAuth } from "@/contexts/AuthContext";
import { ensureDriveLinked } from "@/lib/drive";
import { useNotifications } from "@/contexts/NotificationsContext";

function timeAgo(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "never";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}
function fmtBytes(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  const kb = b / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
}

export default function SyncPill({ className = "", dark = true }) {
  const {
    mode,
    hasFolder,
    hasWorkspace,
    isDriveLinked,
    isDriveAuthed,
    busy,
    receiving: globalReceiving,
    lastSynced,
    lastConflict,
    saveAll,
    reload,
    forceSend,
    healthCheck,
    requestDriveTokenInteractive,
    markSynced,
  } = useStorage();
  const { refreshSmartNotifications } = useNotifications() || {};

  const [sending, setSending] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [forcing, setForcing] = useState(false);
  const [msg, setMsg] = useState("");
  const { getCurrentUser, isSignedIn } = useAuth() || {};
  const [receiveProgress, setReceiveProgress] = useState(null);

  const working = !!(busy || sending || receiving || forcing || globalReceiving);
  const isDark = dark;

  const linkedWhere = isDriveLinked ? "drive" : hasFolder ? "folder" : null;
  const linked = !!linkedWhere;
  const conflict = !!lastConflict;
  const linkedStatus = hasWorkspace && linked;

  const status = useMemo(() => {
    if (!linkedStatus) {
      return {
        color: isDark
          ? "bg-red-500/20 text-red-300 border-red-400/30"
          : "bg-red-50 text-red-800 border-red-200",
        dot: isDark ? "bg-red-400" : "bg-red-400",
        label: "Not linked",
        title: "No storage linked. Connect Drive or a Folder to enable sync.",
      };
    }
    if (conflict) {
      const when = lastSynced?.updatedAt;
      return {
        color: isDark
          ? "bg-amber-500/20 text-amber-200 border-amber-400/30"
          : "bg-amber-50 text-amber-800 border-amber-200",
        dot: isDark ? "bg-amber-400" : "bg-amber-400",
        label: "Conflict",
        title: `Remote appears newer${when ? ` (last local sync ${timeAgo(when)})` : ""}. Receive to pull, or Force to overwrite remote.`,
      };
    }
    if (lastSynced?.updatedAt) {
      return {
        color: isDark
          ? "bg-emerald-500/20 text-emerald-200 border-emerald-400/30"
          : "bg-emerald-50 text-emerald-800 border-emerald-200",
        dot: isDark ? "bg-emerald-400" : "bg-emerald-400",
        label: "Linked",
        title: `Last sync: ${lastSynced.updatedAt}`,
      };
    }
    return {
      color: isDark
        ? "bg-slate-500/20 text-slate-200 border-slate-400/30"
        : "bg-slate-50 text-slate-800 border-slate-200",
      dot: isDark ? "bg-slate-300" : "bg-slate-400",
      label: "Linked (unsynced)",
      title: "Linked but not synced yet on this device.",
    };
  }, [linkedStatus, conflict, lastSynced, isDark]);

  async function handleReceiveClick() {
    console.log("[SyncPill] Receive clicked");
    setMsg("");
    setReceiving(true);

    if (!hasWorkspace) {
      setReceiving(false);
      window.dispatchEvent?.(
        new CustomEvent("mudo:toast", {
          detail: {
            type: "error",
            message: "No workspace selected. Connect a folder or Google Drive first.",
          },
        })
      );
      return;
    }

    if (isDriveLinked) {
      const linkRes = await ensureDriveLinked();
      if (!linkRes?.ok) {
        console.warn("[SyncPill] Receive aborted, Drive not linked or user cancelled", linkRes);
        window.dispatchEvent?.(
          new CustomEvent("mudo:toast", {
            detail: { type: "error", message: "Drive not linked. Please connect Google Drive and try again." },
          })
        );
        setReceiving(false);
        return;
      }
    }

    try {
      const result = await (reload?.() ?? Promise.resolve(null));

      if (!result?.ok) {
        let message = "Could not receive data from your workspace.";
        if (result?.reason === "no-workspace") {
          message = "No workspace selected. Connect a folder or Google Drive first.";
        } else if (result?.reason === "no-vault") {
          message = "No backup vault found in this workspace yet. Try Send first or check your Drive folder.";
        } else if (result?.reason === "exception") {
          message = `Receive failed: ${result?.error || "Unexpected error."}`;
        }

        window.dispatchEvent?.(
          new CustomEvent("mudo:toast", {
            detail: { type: "error", message },
          })
        );
        return;
      }

      try {
        const current = await getCurrentUser?.();
        if (!current && isSignedIn) {
          console.error(
            "[SyncPill] Receive completed but Supabase user is missing – check StorageContext reserved keys."
          );
        } else {
          console.log("[SyncPill] Receive complete, user still signed in", current?.id);
        }
      } catch (err) {
        console.warn("[SyncPill] getCurrentUser after receive failed", err);
      }

      window.dispatchEvent?.(
        new CustomEvent("mudo:toast", {
          detail: { type: "success", message: "Received latest data from your workspace." },
        })
      );

      try {
        const current = await getCurrentUser?.();
        if (!current && isSignedIn) {
          console.warn(
            "[SyncPill] Receive completed but Supabase user missing; check StorageContext reserved keys."
          );
        } else {
          console.log("[SyncPill] Receive complete, user still signed in", current?.id);
        }
      } catch (err) {
        console.warn("[SyncPill] getCurrentUser after receive failed", err);
      }
    } catch (err) {
      console.error("[SyncPill] Receive error", err);
      window.dispatchEvent?.(
        new CustomEvent("mudo:toast", {
          detail: { type: "error", message: "Receive failed. Please try again." },
        })
      );
    } finally {
      setReceiving(false);
    }
  }

  async function onSend() {
    setMsg("");
    setSending(true);
    try {
      const res = await (saveAll?.() ?? Promise.resolve(null));
      if (res?.conflict) {
        setMsg("Remote is newer — Force to overwrite, or Receive first.");
      } else if (res?.where) {
        setMsg(`Sent to ${res.where} (${fmtBytes(res.bytes)})`);
        try { markSynced?.(); } catch {}
        refreshSmartNotifications?.();
      } else {
        setMsg(linked ? "Nothing to send" : "Not linked");
      }
    } finally {
      setSending(false);
    }
  }

  async function onForceSend() {
    setMsg("");
    setForcing(true);
    try {
      const res = await (forceSend?.() ?? Promise.resolve(null));
      if (res?.where) {
        setMsg(`Force sent to ${res.where} (${fmtBytes(res.bytes)})`);
        try { markSynced?.(); } catch {}
        refreshSmartNotifications?.();
      } else {
        setMsg("Force send failed");
      }
    } finally {
      setForcing(false);
    }
  }

  async function onReceive() {
    await handleReceiveClick();
  }

  // Listen for reload progress events
  React.useEffect(() => {
    const handler = (event) => {
      const { phase, processed, total } = event.detail || {};
      if (phase === "start" || phase === "applying") {
        setReceiveProgress({ processed, total });
      } else if (phase === "done" || phase === "error") {
        setReceiveProgress(null);
      }
    };
    window.addEventListener("mudo:reload-progress", handler);
    return () => window.removeEventListener("mudo:reload-progress", handler);
  }, []);

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      {/* Pill */}
      <span
        className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs whitespace-nowrap ${status.color}`}
        title={status.title}
      >
        <span className={`inline-block h-2 w-2 rounded-full ${status.dot}`} />
        <span className="font-medium">{status.label}</span>
        {linkedStatus && (
          <>
            <span className="opacity-75">• {linkedWhere === "drive" ? "Drive" : "Folder"}</span>
            <span className="opacity-75">• last: {timeAgo(lastSynced?.updatedAt)}</span>
          </>
        )}
      </span>

      {/* Controls */}
      <div className="inline-flex items-center gap-2">
        <button
          disabled={!linkedStatus || working}
          onClick={onReceive}
          className={`rounded-md border px-2.5 py-1 text-xs inline-flex items-center gap-1 whitespace-nowrap ${
            isDark
              ? "border-white/20 bg-white/10 text-white hover:bg-white/15 disabled:opacity-50"
              : "border-slate-300 bg-white text-slate-900 hover:bg-slate-100 disabled:opacity-60"
          }`}
          title="Receive (load vault from Drive/Folder into this device)"
        >
          {(receiving || busy || globalReceiving) ? <Spinner /> : null}
          {receiving || globalReceiving ? "Working…" : "Receive"}
        </button>
        <button
          disabled={!linkedStatus || working}
          onClick={onSend}
          className={`rounded-md border px-2.5 py-1 text-xs inline-flex items-center gap-1 whitespace-nowrap ${
            isDark
              ? "border-blue-400/40 bg-blue-600/80 text-white hover:bg-blue-600 disabled:opacity-50"
              : "border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 disabled:opacity-60"
          }`}
          title="Send (save this device’s data to Drive/Folder vault)"
        >
          {(sending || busy) ? <Spinner /> : null}
          {sending ? "Working…" : "Send"}
        </button>
        {conflict && (
          <button
            disabled={!linkedStatus || working}
            onClick={onForceSend}
            className={`rounded-md border px-2.5 py-1 text-xs whitespace-nowrap ${
              isDark
                ? "border-amber-400/50 bg-amber-500/20 text-amber-100 hover:bg-amber-500/30 disabled:opacity-50"
                : "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-60"
            }`}
            title="Force Send (overwrite remote with local)"
          >
            {forcing ? "Working…" : "Force"}
          </button>
        )}
      </div>

      {/* Inline message (announced for SR) */}
      <div className="flex flex-col gap-1 min-w-[120px]">
        <span
          className={`text-[11px] max-w-[28ch] truncate ${
            isDark ? "text-white/70" : "text-slate-700"
          }`}
          aria-live="polite"
        >
          {msg ? `• ${msg}` : ""}
        </span>
        {receiveProgress && receiveProgress.total > 0 && (
          <div
            className={`mt-1 w-full h-1 rounded-full overflow-hidden ${
              isDark ? "bg-white/10" : "bg-slate-200"
            }`}
          >
            <div
              className={`${isDark ? "bg-white/70" : "bg-slate-600"} h-full transition-all`}
              style={{
                width: `${Math.min(
                  100,
                  Math.round((receiveProgress.processed / receiveProgress.total) * 100)
                )}%`,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <svg
      className="h-3.5 w-3.5 animate-spin opacity-80"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
